import React, { useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SliceRow from "./SliceRow";

interface SliceEntry {
  name: string;
  color: string;
  isRollup: boolean;
}

interface SliceCustomizationSectionProps {
  slices: SliceEntry[];
  labelOverrides: Record<string, string>;
  customOrder: string[];
  pinnedStart: string | null;
  pinnedEnd: string | null;
  dataSorting: string;
  colorPalette: string;
  onPinChange: (sliceName: string, position: "none" | "start" | "end") => void;
  onLabelChange: (sliceName: string, label: string) => void;
  onResetLabel: (sliceName: string) => void;
  onCustomOrderChange: (newOrder: string[]) => void;
}

const SliceCustomizationSection: React.FC<SliceCustomizationSectionProps> = ({
  slices,
  labelOverrides,
  customOrder,
  pinnedStart,
  pinnedEnd,
  dataSorting,
  colorPalette,
  onPinChange,
  onLabelChange,
  onResetLabel,
  onCustomOrderChange,
}) => {
  const [editingSliceName, setEditingSliceName] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const isCustomOrder = dataSorting === "custom";
  const isCustomPalette = colorPalette === "Custom (per-slice)";

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Build ordered list for display
  const orderedSlices = useMemo(() => {
    if (!isCustomOrder || customOrder.length === 0) return slices;
    const orderIndex = new Map(customOrder.map((n, i) => [n, i]));
    return [...slices].sort((a, b) => {
      const ai = orderIndex.has(a.name) ? orderIndex.get(a.name)! : slices.length;
      const bi = orderIndex.has(b.name) ? orderIndex.get(b.name)! : slices.length;
      return ai - bi;
    });
  }, [slices, isCustomOrder, customOrder]);

  const sortableIds = orderedSlices.map((s) => s.name);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(active.id as string);
    const newIndex = sortableIds.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onCustomOrderChange(arrayMove(sortableIds, oldIndex, newIndex));
    }
  };

  const handleStartEdit = (sliceName: string, currentLabel: string) => {
    setEditingSliceName(sliceName);
    setEditValue(currentLabel);
  };

  const handleSaveEdit = () => {
    if (editingSliceName && editValue.trim()) {
      onLabelChange(editingSliceName, editValue.trim());
    }
    setEditingSliceName(null);
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingSliceName(null);
    setEditValue("");
  };

  const handleResetLabel = (sliceName: string) => {
    onResetLabel(sliceName);
    if (editingSliceName === sliceName) {
      setEditingSliceName(null);
      setEditValue("");
    }
  };

  if (slices.length === 0) return null;

  return (
    <Accordion defaultExpanded sx={{ boxShadow: "none", border: "1px solid #eee" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Slice Customization
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              maxHeight: 400,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              {orderedSlices.map((slice) => {
                const overrideLabel = labelOverrides[slice.name];
                const displayName = overrideLabel ?? slice.name;
                const pinPosition: "none" | "start" | "end" =
                  pinnedStart === slice.name
                    ? "start"
                    : pinnedEnd === slice.name
                      ? "end"
                      : "none";
                const isEditing = editingSliceName === slice.name;

                return (
                  <SliceRow
                    key={slice.name}
                    name={slice.name}
                    displayName={displayName}
                    isOverridden={Boolean(overrideLabel)}
                    isRollup={slice.isRollup}
                    pinPosition={pinPosition}
                    isCustomOrder={isCustomOrder}
                    showPinControl={!isCustomOrder}
                    showColorSwatch={isCustomPalette}
                    sliceColor={slice.color}
                    isEditing={isEditing}
                    editValue={editValue}
                    onPinChange={(pos) => onPinChange(slice.name, pos)}
                    onStartEdit={() => handleStartEdit(slice.name, displayName)}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onEditValueChange={setEditValue}
                    onResetLabel={() => handleResetLabel(slice.name)}
                  />
                );
              })}
            </SortableContext>
          </Box>
        </DndContext>
      </AccordionDetails>
    </Accordion>
  );
};

export default SliceCustomizationSection;
